import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import StatusIcon, { rollupIndicator, StatusIndicator, statusIndicator } from './StatusIcon';

interface RailRow {
  key: string;
  label: string;
  indicator: StatusIndicator;
}

interface RailStep extends RailRow {
  // Shown nested only when a category has more than one action (design §12 roll-up).
  children: RailRow[];
}

// One row per category, with action children only where a category has more than
// one action.
function buildSteps(workflow: Workflow): RailStep[] {
  return workflow.actionsByCategory().map(({ category, actions }) => ({
    key: category.key,
    label: category.name,
    indicator: rollupIndicator(actions),
    children:
      actions.length > 1
        ? actions.map((action) => ({
            key: action.id,
            label: action.displayLabel,
            indicator: statusIndicator(action),
          }))
        : [],
  }));
}

const isMuted = (indicator: StatusIndicator) => indicator === 'pending' || indicator === 'muted';

export default function ProgressRail({ workflow }: { workflow: Workflow }) {
  const steps = buildSteps(workflow);

  return (
    <Rail data-testid='progress-rail'>
      <BodySmallBold>Progress</BodySmallBold>
      {workflow.createdAt && <LastUpdate>Referral created on {simpleDateAtTime(workflow.createdAt)}</LastUpdate>}
      <Steps>
        {steps.map((step) => (
          <div key={step.key}>
            <StepRow>
              <StatusIcon indicator={step.indicator} size={18} />
              <StepLabel $muted={isMuted(step.indicator)}>{step.label}</StepLabel>
            </StepRow>
            {step.children.map((child) => (
              <ChildRow key={child.key}>
                <StatusIcon indicator={child.indicator} size={14} />
                <ChildLabel $muted={isMuted(child.indicator)}>{child.label}</ChildLabel>
              </ChildRow>
            ))}
          </div>
        ))}
      </Steps>
    </Rail>
  );
}

const Rail = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  padding: 20px;
`;

const LastUpdate = styled(BodySmall)`
  display: block;
  margin-top: 6px;
  color: ${colors.black50};
`;

const Steps = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChildRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-left: 28px;
`;

const StepLabel = styled(BodySmall)<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.black50 : colors.black)};
  font-weight: 700;
`;

const ChildLabel = styled(BodySmall)<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.black50 : colors.black75)};
`;
